import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Paper,
  IconButton,
  Snackbar,
  Alert,
  AlertColor,
  Chip,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  SelectChangeEvent,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Lock as LockIcon,
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
  Label as LabelIcon,
} from '@mui/icons-material';
import { format, isToday, parseISO } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { usePasskey } from '../contexts/PasskeyContext';
import {
  getDiaryDates,
  getDiaryByDate,
  createDiaryEntry,
  updateDiaryEntry,
  getDiaryLabels,
  DiaryEntry,
} from '../services/diaryService';
import NotebookEditor from '../components/NotebookEditor';
import LabelSelector from '../components/LabelSelector';
import Layout from '../components/Layout';

const Diary: React.FC = () => {
  const { currentUser } = useAuth();
  const { passkey } = usePasskey();

  const [dates, setDates] = useState<{ id: string; date: string; hasContent: boolean; labels: string[] }[]>([]);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [currentEntry, setCurrentEntry] = useState<DiaryEntry | null>(null);
  const [content, setContent] = useState('');
  const [entryLabels, setEntryLabels] = useState<string[]>([]);
  const [availableLabels, setAvailableLabels] = useState<string[]>([]);
  const [selectedLabelFilter, setSelectedLabelFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [loadingEntry, setLoadingEntry] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: AlertColor }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => format(new Date(), 'yyyy-MM-dd');

  // Load available labels
  const loadAvailableLabels = useCallback(async () => {
    if (!currentUser) return;
    
    try {
      const fetchedLabels = await getDiaryLabels(currentUser.uid);
      setAvailableLabels(fetchedLabels);
    } catch (error) {
      console.error('Error loading labels:', error);
    }
  }, [currentUser]);

  // Load diary dates
  const loadDates = useCallback(async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      const labelFilter = selectedLabelFilter === 'all' ? null : selectedLabelFilter;
      const { dates: fetchedDates } = await getDiaryDates(currentUser.uid, 30, undefined, labelFilter);
      
      // Check if today exists in the list (only when not filtering)
      const today = getTodayDate();
      const hasToday = fetchedDates.some((d) => d.date === today);
      
      if (!hasToday && selectedLabelFilter === 'all') {
        // Add today to the beginning only when showing all
        setDates([{ id: '', date: today, hasContent: false, labels: [] }, ...fetchedDates]);
      } else {
        setDates(fetchedDates);
      }
    } catch (error) {
      console.error('Error loading diary dates:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser, selectedLabelFilter]);

  const handleLabelFilterChange = (event: SelectChangeEvent<string>) => {
    setSelectedLabelFilter(event.target.value);
  };

  useEffect(() => {
    loadDates();
    loadAvailableLabels();
  }, [loadDates, loadAvailableLabels]);

  // Load entry for selected date
  const loadEntry = useCallback(async (date: string) => {
    if (!currentUser || !passkey) return;

    try {
      setLoadingEntry(true);
      const entry = await getDiaryByDate(currentUser.uid, date, passkey);

      if (entry) {
        setCurrentEntry(entry);
        setContent(entry.content);
        setEntryLabels(entry.labels || []);
      } else {
        // No entry exists for this date
        setCurrentEntry(null);
        setContent('');
        setEntryLabels([]);
      }
    } catch (error) {
      console.error('Error loading diary entry:', error);
    } finally {
      setLoadingEntry(false);
    }
  }, [currentUser, passkey]);

  // Handle date selection
  const handleDateSelect = (date: string) => {
    setSelectedDate(date);
    loadEntry(date);
  };

  // Auto-save function
  const autoSave = useCallback(async () => {
    if (!currentUser || !passkey || !selectedDate) return;

    try {
      setSaveStatus('saving');

      if (currentEntry) {
        // Update existing entry
        await updateDiaryEntry(currentEntry.id, { content, labels: entryLabels }, passkey);
      } else {
        // Create new entry
        const newId = await createDiaryEntry(
          currentUser.uid,
          { date: selectedDate, content, labels: entryLabels },
          passkey
        );
        setCurrentEntry({
          id: newId,
          userId: currentUser.uid,
          date: selectedDate,
          content,
          labels: entryLabels,
          createdAt: null as any,
          updatedAt: null as any,
        });
        
        // Update dates list
        setDates((prev) =>
          prev.map((d) =>
            d.date === selectedDate ? { ...d, id: newId, hasContent: true, labels: entryLabels } : d
          )
        );
      }

      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2000);
      
      // Refresh available labels
      loadAvailableLabels();
    } catch (error) {
      console.error('Error saving diary entry:', error);
      setSaveStatus(null);
      setSnackbar({ open: true, message: 'Error saving diary', severity: 'error' });
    }
  }, [currentUser, passkey, selectedDate, currentEntry, content, entryLabels, loadAvailableLabels]);

  // Debounced auto-save
  const debouncedAutoSave = useCallback(() => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    autoSaveTimeoutRef.current = setTimeout(() => {
      autoSave();
    }, 1000);
  }, [autoSave]);

  // Auto-save on content change
  useEffect(() => {
    if (selectedDate && !loadingEntry && !currentEntry?.isLocked) {
      debouncedAutoSave();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  // Format date for display
  const formatDateDisplay = (dateStr: string) => {
    const date = parseISO(dateStr);
    if (isToday(date)) {
      return 'Today';
    }
    return format(date, 'EEEE, MMMM d, yyyy');
  };

  const formatDateShort = (dateStr: string) => {
    const date = parseISO(dateStr);
    if (isToday(date)) {
      return 'Today';
    }
    return format(date, 'MMM d, yyyy');
  };

  // Render date list
  const renderDateList = () => (
    <Box sx={{ height: '100%', overflow: 'auto' }}>
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          Diary
        </Typography>
        
        {/* Labels Dropdown Filter */}
        <FormControl size="small" fullWidth>
          <InputLabel id="diary-label-filter-label">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LabelIcon sx={{ fontSize: 18 }} />
              Filter by Label
            </Box>
          </InputLabel>
          <Select
            labelId="diary-label-filter-label"
            id="diary-label-filter"
            value={selectedLabelFilter}
            label="Filter by Label"
            onChange={handleLabelFilterChange}
            sx={{
              '& .MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
              },
            }}
          >
            <MenuItem value="all">
              <em>All Entries</em>
            </MenuItem>
            {availableLabels.map((label) => (
              <MenuItem key={label} value={label}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        {selectedLabelFilter !== 'all' && (
          <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
            Showing: <strong>{selectedLabelFilter}</strong>
          </Typography>
        )}
      </Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <List>
          {dates.map((item) => (
            <ListItem key={item.date} disablePadding>
              <ListItemButton
                selected={selectedDate === item.date}
                onClick={() => handleDateSelect(item.date)}
                sx={{
                  borderRadius: 2,
                  mx: 1,
                  mb: 0.5,
                  '&.Mui-selected': {
                    backgroundColor: 'primary.light',
                  },
                }}
              >
                <ListItemIcon>
                  <CalendarIcon
                    color={isToday(parseISO(item.date)) ? 'primary' : 'inherit'}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={formatDateShort(item.date)}
                  secondary={isToday(parseISO(item.date)) ? 'Write about your day' : null}
                  primaryTypographyProps={{
                    fontWeight: isToday(parseISO(item.date)) ? 600 : 400,
                  }}
                />
                {item.hasContent && (
                  <Chip size="small" label="•" sx={{ minWidth: 24 }} />
                )}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );

  // Render diary editor
  const renderEditor = () => {
    if (!selectedDate) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            p: 4,
          }}
        >
          <CalendarIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Select a date to view or write your diary
          </Typography>
        </Box>
      );
    }

    if (loadingEntry) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (currentEntry?.isLocked) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            p: 4,
          }}
        >
          <LockIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            This diary entry is locked
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            It was encrypted with a different passkey
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
          <IconButton onClick={() => setSelectedDate(null)} sx={{ display: { md: 'none' } }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
            {formatDateDisplay(selectedDate)}
          </Typography>
          
          {/* Labels in header */}
          <Box sx={{ flexGrow: 1, maxWidth: 300 }}>
            <LabelSelector
              availableLabels={availableLabels}
              selectedLabels={entryLabels}
              onLabelsChange={(newLabels) => {
                setEntryLabels(newLabels);
                debouncedAutoSave();
              }}
            />
          </Box>
          
          {saveStatus === 'saved' && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                height: 24,
                borderRadius: '50%',
                bgcolor: 'success.main',
                color: 'white',
              }}
            >
              <CheckIcon sx={{ fontSize: 16 }} />
            </Box>
          )}
          {saveStatus === 'saving' && <CircularProgress size={20} />}
        </Box>

        <NotebookEditor
          content={content}
          onChange={setContent}
          placeholder="Dear diary..."
          paperColor="yellow"
        />
      </Box>
    );
  };

  return (
    <Layout>
      <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)' }}>
        {/* Date list sidebar */}
        <Paper
          elevation={0}
          sx={{
            width: 300,
            borderRight: '1px solid',
            borderColor: 'divider',
            display: { xs: selectedDate ? 'none' : 'block', md: 'block' },
          }}
        >
          {renderDateList()}
        </Paper>

        {/* Editor area */}
        <Box
          sx={{
            flexGrow: 1,
            display: { xs: selectedDate ? 'block' : 'none', md: 'block' },
          }}
        >
          {renderEditor()}
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default Diary;
